import React, { useEffect, useState } from "react";
import { Table, DatePicker, Spin, Tag, Space, Typography, Avatar } from "antd";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { UserOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import PageNavigation from "../Layout/PageNavigation";
import ExcelExportButton from "./ExcelExportButton";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface CurrencyAmount {
    currency: string;
    amount: number;
    transactions: number;
}

interface CustomerBalance {
    currency: string;
    balance: number;
}

interface TopCustomer {
    _id: string;
    totalsByCurrency: CurrencyAmount[];
    transactions: number;
    customer: {
        _id: string;
        name: string;
        phone?: string;
        address?: string;
        balances?: CustomerBalance[];
    };
}

const currencyColor = (currency: string) =>
    currency === "USD" ? "blue" : currency === "IQD" ? "gold" : "default";

const balanceColor = (balance: number) => {
    if (balance > 0) return "#f59e0b";   // owes money (positive = customer owes)
    if (balance < 0) return "#10b981";   // credit
    return "#94a3b8";
};

const TopCustomersTable: React.FC = () => {
    const { fetchTopCustomers } = useReportApi();
    const { t } = useTranslation();
    const [data, setData] = useState<TopCustomer[]>([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf("year"),
        dayjs(),
    ]);

    useEffect(() => {
        setLoading(true);
        fetchTopCustomers({
            startDate: dateRange[0].toISOString(),
            endDate: dateRange[1].toISOString(),
            limit: 10,
        })
            .then((res: TopCustomer[]) => setData(res))
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, [dateRange]);

    const columns = [
        {
            title: "#",
            key: "rank",
            width: 48,
            render: (_: any, __: any, idx: number) => (
                <Avatar
                    size={28}
                    style={{
                        background: idx === 0 ? "#f59e0b" : idx === 1 ? "#94a3b8" : idx === 2 ? "#b45309" : "#e2e8f0",
                        color: idx < 3 ? "#fff" : "#475569",
                        fontWeight: 700,
                        fontSize: 13,
                    }}
                >
                    {idx + 1}
                </Avatar>
            ),
        },
        {
            title: t("customer"),
            key: "customer",
            render: (_: any, record: TopCustomer) => (
                <Space direction="vertical" size={0}>
                    <Space size={6}>
                        <UserOutlined style={{ color: "#2850a7" }} />
                        <Text strong style={{ fontSize: 14 }}>
                            {record.customer?.name || "-"}
                        </Text>
                    </Space>
                    {record.customer?.phone && (
                        <Space size={4}>
                            <PhoneOutlined style={{ color: "#64748b", fontSize: 11 }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {record.customer.phone}
                            </Text>
                        </Space>
                    )}
                    {record.customer?.address && (
                        <Space size={4}>
                            <EnvironmentOutlined style={{ color: "#64748b", fontSize: 11 }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {record.customer.address}
                            </Text>
                        </Space>
                    )}
                </Space>
            ),
        },
        {
            title: t("qty"),
            dataIndex: "transactions",
            key: "transactions",
            width: 100,
            render: (v: number) => (
                <Tag color="purple" style={{ fontWeight: 600 }}>
                    {v} {t("invoice") || "invoices"}
                </Tag>
            ),
        },
        {
            title: t("totalAmount"),
            key: "totalsByCurrency",
            render: (_: any, record: TopCustomer) => (
                <Space direction="vertical" size={2}>
                    {(record.totalsByCurrency || []).map((c) => (
                        <Space key={c.currency} size={4}>
                            <Tag color={currencyColor(c.currency)} style={{ fontWeight: 700, minWidth: 42, textAlign: "center" }}>
                                {c.currency}
                            </Tag>
                            <Text strong style={{ fontSize: 14 }}>
                                {c.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </Text>
                        </Space>
                    ))}
                </Space>
            ),
        },
        {
            title: t("balance") || "Balance",
            key: "balance",
            render: (_: any, record: TopCustomer) => {
                const balances = record.customer?.balances?.filter(b => b.balance !== 0);
                if (!balances || balances.length === 0)
                    return <Text type="secondary">—</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        {balances.map((b) => (
                            <Space key={b.currency} size={4}>
                                <Tag color={currencyColor(b.currency)} style={{ minWidth: 42, textAlign: "center" }}>
                                    {b.currency}
                                </Tag>
                                <Text strong style={{ color: balanceColor(b.balance), fontSize: 13 }}>
                                    {b.balance > 0 ? "+" : ""}
                                    {b.balance.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                </Text>
                            </Space>
                        ))}
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <PageNavigation title={t("top_customers") as string} />
            <Space style={{ marginBottom: 12 }} wrap>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1])
                            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                    }}
                />
                <ExcelExportButton<TopCustomer>
                    fileName="top-customers"
                    sheetName={t("top_customers") as string}
                    columns={[
                        { header: t("customer") as string, key: (row) => row.customer?.name || "-", width: 28 },
                        { header: t("phone") as string, key: (row) => row.customer?.phone || "-", width: 16 },
                        { header: t("qty") as string, key: "transactions", width: 12 },
                        {
                            header: t("totalAmount") as string,
                            key: (row) =>
                                (row.totalsByCurrency || [])
                                    .map((c) => `${c.currency} ${c.amount.toLocaleString()}`)
                                    .join(" | "),
                            width: 30,
                        },
                        {
                            header: (t("balance") as string) || "Balance",
                            key: (row) =>
                                (row.customer?.balances || [])
                                    .filter((b) => b.balance !== 0)
                                    .map((b) => `${b.currency} ${b.balance.toLocaleString()}`)
                                    .join(" | ") || "-",
                            width: 26,
                        },
                    ]}
                    rows={data}
                    title={[
                        t("top_customers") as string,
                        `${dateRange[0].format("YYYY-MM-DD")} → ${dateRange[1].format("YYYY-MM-DD")}`,
                    ]}
                />
            </Space>
            <Spin spinning={loading}>
                <Table
                    rowKey="_id"
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    size="middle"
                    bordered
                    rowClassName={(_, idx) =>
                        idx === 0 ? "top-customer-gold" : ""
                    }
                    style={{ borderRadius: 10, overflow: "hidden" }}
                />
            </Spin>
            <style>{`
                .top-customer-gold td {
                    background: #fffbeb !important;
                }
            `}</style>
        </>
    );
};

export default TopCustomersTable;
